import React, { useEffect, useReducer } from "react";

import FlagPicker from "../setup/FlagPicker";
import { SHOP_ITEMS, EXCLUSIVE_ITEMS, RARITIES } from "../../shared/shop/store";
import { flagName, flagUrlSquare } from "../../shared/flags";
import { equippedHeadStyle, equippedVictoryAnim, subscribeLocalPlayers } from "../../shared/local-players";
import { subscribeCosmeticsGate } from "../../shared/cosmetics-gate";
import { getEquippedBoardSkin } from "../../game-canvas/board-skins";
import type { HeadStyleType } from "../../game-canvas/head-styles";
import type { VictoryAnimType } from "../../game-canvas/victory-anim";
import { BoardPreview, HeadPreview, VictoryPreview } from "./ShopPage";
import s from "./Equipment.module.scss";

// What you wear: one card per kind of cosmetic (flag, worm head, arena,
// victory celebration). Each shows what is on, live as in the shop, in its
// rarity's colour (the name is the tooltip); an empty one is a plus. A tap opens that
// department of the shop (the flag card opens the flag picker when it can).
// Used by the phone's player card and by your own profile.

const ITEMS = [...SHOP_ITEMS, ...EXCLUSIVE_ITEMS];
const itemOf = (id: string | null) => (id ? ITEMS.find((i) => i.id === id) ?? null : null);
// the shop's rarity ladder (see ShopPage's RARITY_COLOR)
const RARITY_COLOR: Record<string, string> = { common: "#9fb0c8", rare: "#28c4d4", epic: "#ac5cff", legendary: "#ffcf4d" };

type Slot = { kind: "head" | "board" | "victory"; label: string; tab: string; equipped: () => string | null; preview: (id: string) => React.ReactElement };
const SLOTS: Slot[] = [
  { kind: "head", label: "Head", tab: "heads", equipped: equippedHeadStyle, preview: (id) => <HeadPreview type={id as HeadStyleType} /> },
  { kind: "board", label: "Arena", tab: "arena", equipped: getEquippedBoardSkin, preview: (id) => <BoardPreview id={id} /> },
  { kind: "victory", label: "Victory", tab: "victory", equipped: equippedVictoryAnim, preview: (id) => <VictoryPreview type={id as VictoryAnimType} flag={null} /> },
];

const PlusIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden="true"><path d="M12 6v12M6 12h12" /></svg>);

export default function Equipment({ flag, onFlag, onShop, size = "sm", colour }: {
  flag: string;
  // with it, the flag card is the flag picker; without, it leads to the shop's flags
  onFlag?: (code: string) => void;
  // without it (a game site's build: no Store) only the colour and the flag show
  onShop?: (tab: string) => void;
  size?: "sm" | "lg";
  // with it, a fifth card leads the row: your worm's colour (a tap is the caller's to answer)
  colour?: { color: string; onClick: () => void };
}) {
  // re-read when an equip changes elsewhere (the shop, signing in or out)
  const [, bump] = useReducer((n: number) => n + 1, 0);
  useEffect(() => subscribeLocalPlayers(bump), []);
  useEffect(() => subscribeCosmeticsGate(bump), []);

  const flagItem = itemOf(flag);
  const card = (key: string, label: string, name: string | null, rarity: string | null, well: React.ReactNode, onClick: (() => void) | null, aria: string) => {
    const style = rarity ? ({ "--c": RARITY_COLOR[rarity] } as React.CSSProperties) : undefined;
    const body = (
      <>
        <span className={s.well}>{well}</span>
        <span className={s.text}><small>{label}</small></span>
      </>
    );
    const cls = `${s.card} ${name ? "" : s.empty} ${rarity ? s.rare : ""}`;
    return onClick
      ? <button key={key} type="button" className={cls} style={style} onClick={onClick} aria-label={aria} title={name ? (rarity ? `${name} · ${RARITIES[rarity as keyof typeof RARITIES]?.label}` : name) : undefined}>{body}</button>
      : <div key={key} className={cls} style={style}>{body}</div>;
  };

  return (
    <div className={`${s.equip} ${size === "lg" ? s.lg : ""} ${colour ? s.five : ""}`}>
      {colour && card("colour", "Colour", "colour", null, <i className={s.colour} style={{ background: colour.color }} />, colour.onClick, "Colour")}
      {onFlag ? (
        // the flag picker's own button is the card's picture
        <div className={`${s.card} ${flagItem?.rarity ? s.rare : ""}`} style={flagItem?.rarity ? ({ "--c": RARITY_COLOR[flagItem.rarity] } as React.CSSProperties) : undefined}>
          <span className={`${s.well} ${s.flagWell}`}><FlagPicker code={flag} onChange={onFlag} fill /></span>
          <span className={s.text}><small>Flag</small></span>
        </div>
      ) : card("flag", "Flag", flagName(flag), flagItem?.rarity ?? null, <img src={flagUrlSquare(flag)} alt="" />, onShop ? () => onShop("flags") : null, `Flag: ${flagName(flag)}, to the shop`)}
      {onShop && SLOTS.map((slot) => {
        const id = slot.equipped();
        const item = itemOf(id);
        return card(
          slot.kind, slot.label, id ? item?.label ?? id : null, item?.rarity ?? null,
          id ? slot.preview(id) : <span className={s.add}><PlusIcon /></span>,
          () => onShop(slot.tab),
          `${slot.label}: ${id ? item?.label ?? id : "none"}, to the shop`,
        );
      })}
    </div>
  );
}
